import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import { FaSearch } from "react-icons/fa";

const PobData = () => {
    const navigate = useNavigate();

    // State for dropdown options
    const [states, setStates] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [employees, setEmployees] = useState([]);
    
    // State for selected filters
    const [selectedState, setSelectedState] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("All");
    const [selectedEmployee, setSelectedEmployee] = useState("All");
    
    // State for table data and UI
    const [tableData, setTableData] = useState([]);
    const [showContainers, setShowContainers] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const inputRef = useRef(null);

    const handleExpandSearch = () => {
        setIsSearchExpanded(true);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    // Handle search submit
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (showContainers && hasInitialLoad) {
            handleGoClick();
        }
    };

    // Month/Year dropdown setup
    const getMonthYearOptions = () => {
        const months = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        const currentYear = new Date().getFullYear();
        return months.map((month, index) => ({
            label: `${month} ${currentYear}`,
            value: `${String(index + 1).padStart(2, '0')}-${currentYear}`
        }));
    };
    
    const monthYearOptions = getMonthYearOptions();
    const [pobMonthYear, setPobMonthYear] = useState(monthYearOptions[new Date().getMonth()].value);

    const fetchEmployees = useCallback(async (stateId, divisionName) => {
        if (!stateId) return;
      
        try {
            let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
            if (divisionName && divisionName !== "All") {
                url += `&division=${encodeURIComponent(divisionName)}`;
            }
        
            const res = await axios.get(url, {
                headers: getAuthHeaders()
            });
            setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...res.data]);
            setSelectedEmployee("All");
        } catch (err) {
            console.error("Error fetching employees:", err);
            setEmployees([]);
            setError("Failed to load employees");
        }
    }, []);

    // Restore state from localStorage on component mount
    useEffect(() => {
        const savedState = localStorage.getItem('pobDataState');
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                
                // Restore all state at once
                setSelectedState(state.selectedState || "");
                setSelectedDivision(state.selectedDivision || "All");
                setSelectedEmployee(state.selectedEmployee || "All");
                setPobMonthYear(state.pobMonthYear || monthYearOptions[new Date().getMonth()].value);
                setTableData(state.tableData || []);
                setShowContainers(Boolean(state.tableData && state.tableData.length > 0));
                setHasInitialLoad(Boolean(state.tableData && state.tableData.length > 0));
                
                // Restore dropdown data if state is selected
                if (state.selectedState) {
                    // Fetch divisions
                    axios.get(`${BASE_URL}/api/divisions?state_id=${state.selectedState}`, {
                        headers: getAuthHeaders()
                    })
                    .then(response => {
                        setDivisions([{ id: "All", name: "All" }, ...response.data]);
                    })
                    .catch(err => {
                        console.error("Error restoring divisions:", err);
                        setError("Failed to restore divisions");
                    });
                    
                    // Fetch employees
                    const divisionName = state.selectedDivision === "All" ? "All" : 
                        (state.divisions?.find(d => d.id === state.selectedDivision)?.name || "All");
                    fetchEmployees(state.selectedState, divisionName);
                }
                
                localStorage.removeItem('pobDataState');
            } catch (err) {
                console.error("Error restoring state:", err);
                localStorage.removeItem('pobDataState');
            }
        }
    }, [fetchEmployees, monthYearOptions]);

    // Fetch states on component mount
    useEffect(() => {
        const fetchStates = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/states`, {
                    headers: getAuthHeaders()
                });
                setStates(response.data);
            } catch (err) {
                console.error("Error fetching states:", err);
                setError("Failed to load states");
            }
        };
        fetchStates();
    }, []);

    // Fetch divisions when state changes
    useEffect(() => {
        const fetchDivisions = async () => {
            if (selectedState) {
                try {
                    const response = await axios.get(
                        `${BASE_URL}/api/divisions?state_id=${selectedState}`,
                        { headers: getAuthHeaders() }
                    );
                    setDivisions([{ id: "All", name: "All" }, ...response.data]);
                    setSelectedDivision("All");
                } catch (err) {
                    console.error("Error fetching divisions:", err);
                    setError("Failed to load divisions");
                }
            } else {
                setDivisions([]);
                setSelectedDivision("All");
            }
        };
        fetchDivisions();
    }, [selectedState]);

    const handleStateChange = async (stateId) => {
        setSelectedState(stateId);
        setSelectedDivision("All");
        setSelectedEmployee("All");
        setError(null);
    
        if (stateId) {
            // fetch divisions
            try {
                const divisionRes = await axios.get(`${BASE_URL}/api/divisions?state_id=${stateId}`, {
                    headers: getAuthHeaders()
                });
                setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
            } catch (err) {
                console.error("Error loading divisions", err);
                setError("Failed to load divisions");
            }
        
            // fetch employees based only on state
            fetchEmployees(stateId, "All");
        } else {
            setDivisions([]);
            setEmployees([]);
        }
    };
    
    const handleDivisionChange = async (divisionId) => {
        setSelectedDivision(divisionId);
        setSelectedEmployee("All");
        setError(null);
    
        const division = divisions.find(d => String(d.id) === divisionId);
        const divisionName = division?.name || "All";
        fetchEmployees(selectedState, divisionName);
    };

    const handleGoClick = useCallback(async () => {
        if (!selectedState) {
            setError("Please select a state first");
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            // Parse month and year from pobMonthYear (format: "MM-YYYY")
            const [month, year] = pobMonthYear.split('-');
            
            let url = `${BASE_URL}/api/summary?state_id=${selectedState}&month=${month}&year=${year}`;

            if (selectedDivision && selectedDivision !== "All") {
                const division = divisions.find(d => String(d.id) === selectedDivision);
                if (division?.name) url += `&division=${encodeURIComponent(division.name)}`;
            }

            if (selectedEmployee && selectedEmployee !== "All") {
                url += `&employee_id=${selectedEmployee}`;
            }

            // Add search query if exists
            if (searchQuery) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }

            const response = await axios.get(url, { headers: getAuthHeaders() });
            
            // Handle response data
            if (response.data) {
                if (Array.isArray(response.data)) {
                    // Handle case where API returns array directly
                    setTableData(response.data);
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    // Handle paginated response
                    setTableData(response.data.data);
                } else {
                    // Handle other response formats
                    setTableData([]);
                }
            } else {
                setTableData([]);
            }
            
            setShowContainers(true);
            setHasInitialLoad(true);
        } catch (err) {
            console.error("Failed to fetch summary", err);
            if (err.response) {
                setError(`Server error: ${err.response.status}. ${err.response.data?.message || 'Please try again.'}`);
            } else if (err.request) {
                setError("Network error. Please check your connection and try again.");
            } else {
                setError("Something went wrong while fetching data. Please try again.");
            }
            setTableData([]);
        } finally {
            setIsLoading(false);
        }
    }, [
        selectedState,
        selectedDivision,
        selectedEmployee,
        pobMonthYear,
        searchQuery,
        divisions
    ]);

    // Handle search query changes with debouncing
    useEffect(() => {
        if (showContainers && hasInitialLoad && searchQuery !== "") {
            const timeoutId = setTimeout(() => {
                handleGoClick();
            }, 500); // Debounce search by 500ms

            return () => clearTimeout(timeoutId);
        }
    }, [searchQuery, showContainers, hasInitialLoad, handleGoClick]);

    const handleNavigation = (path, employeeId) => {
        const filterState = {
            selectedState,
            selectedDivision,
            selectedEmployee,
            pobMonthYear,
            tableData,
            divisions, 
            employees  
        };
        // Parse month and year from pobMonthYear (format: "MM-YYYY")
        const [month, year] = pobMonthYear.split('-');
        
        // Navigate with month/year as query params
        navigate(`${path}?month=${month}&year=${year}`);
        localStorage.setItem('pobDataState', JSON.stringify(filterState));
    };

    const handleGoButtonClick = () => {
        setShowContainers(true);
        handleGoClick();
    };

    const handleRetry = () => {
        setError(null);
        handleGoClick();
    };

    return (
        <div className="pob-data-main-container">

            <div className="pob-data-filter-container">
                <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
                    <span className="text-blue-700"> Monthly </span>
                    <span className="text-blue-500">Count</span>
                </h1>

                <div className="pob-data-filter-row1">
                    <label className="pob-data-label">State:</label>
                    <select
                        className="pob-data-dropdown"
                        value={selectedState}
                        onChange={(e) => handleStateChange(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="">Select State</option>
                        {states.map((state) => (
                            <option key={state.id} value={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </select>

                    <label className="pob-data-label">Division:</label>
                    <select
                        className="pob-data-dropdown"
                        value={selectedDivision}
                        onChange={(e) => handleDivisionChange(e.target.value)}
                        disabled={!selectedState || isLoading}
                    >
                        {divisions.map((division) => (
                            <option key={division.id} value={division.id}>
                                {division.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="pob-data-filter-row2">
                    <label className="pob-data-label">Employee:</label>
                    <select
                        className="pob-data-dropdown"
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        disabled={!selectedState || isLoading}
                    >
                        {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                                {emp.id === "All" ? "All" : `${emp.first_name} ${emp.last_name} | ${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name}`}
                            </option>
                        ))}
                    </select>

                    <label className="pob-data-label">Month / Year:</label>
                    <select
                        className="pob-data-dropdown"
                        value={pobMonthYear}
                        onChange={(e) => setPobMonthYear(e.target.value)}
                        disabled={isLoading}
                    >
                        {monthYearOptions.map((option, idx) => (
                            <option key={idx} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <button 
                    className="pob-data-go-button" 
                    onClick={handleGoButtonClick}
                    disabled={isLoading || !selectedState}
                >
                    {isLoading ? "Loading..." : "GO"}
                </button>

                {error && (
                    <div className="pob-data-error">
                        {error}
                        <button onClick={handleRetry} className="ml-2 text-blue-600 hover:text-blue-800">
                            Retry
                        </button>
                    </div>
                )}
            </div>

            {showContainers && (
                <div className="pob-data-table-container">
                    <div className="pob-data-title3-container">
                        <h2 className="pob-data-title3 text-center">
                            POB Data - (
                            {selectedState && `State: ${states.find(s => String(s.id) === String(selectedState))?.name || 'All States'}`}
                            {" | Division: "}
                            {selectedDivision === "All"
                                ? "All Divisions"
                                : divisions.find(d => String(d.id) === String(selectedDivision))?.name || "Unknown Division"}

                            {" | Employee: "}
                            {selectedEmployee === "All"
                                ? "All Employees"
                                : (() => {
                                    const emp = employees.find(e => String(e.id) === String(selectedEmployee));
                                    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown Employee";
                                })()}
                            )
                        </h2>
                        <div className="relative mb-4 flex justify-end">
                            <form
                                onSubmit={handleSearchSubmit}
                                className="flex items-center gap-2"
                            >
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Search employees..."
                                    className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        isSearchExpanded || searchQuery
                                            ? "w-64 opacity-100"
                                            : "w-0 opacity-0 px-0 py-0"
                                    }`}
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                    }}
                                    onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                                />
                                <button
                                    type="button"
                                    onClick={handleExpandSearch}
                                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                >
                                    <FaSearch className="w-5 h-5" />
                                </button>
                            </form>
                        </div>
                    </div>
                    
                    <div className="pob-data-table-wrapper">
                        {isLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="text-lg text-gray-600">Loading data...</div>
                            </div>
                        ) : (
                            <table className="pob-data-table">
                                <thead className="text-white">
                                    <tr>
                                        <th>S.No</th>
                                        <th>EMP ID</th>
                                        <th>Employee</th>
                                        <th>State</th>
                                        <th>HQ</th>
                                        <th>Division</th>
                                        <th>Designation</th>
                                        <th>DOJ</th>
                                        <th>Manager</th>
                                        <th>No Of Doctors</th>
                                        <th>No Of Retailers</th>
                                        <th>No Of Stockist</th>
                                        <th>No Of Route</th>
                                        <th>POB Total</th>
                                        <th>POB DateWise</th>
                                        <th>Stockist Wise</th>
                                        <th>Cumulative <br/>Stockist Wise</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.length > 0 ? (
                                        tableData.map((row, index) => (
                                            <tr key={`${row.employee_id}-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{row.employee_id}</td>
                                                <td>{row.employee_name}</td>
                                                <td>{row.state}</td>
                                                <td>{row.hq}</td>
                                                <td>{row.division}</td>
                                                <td>{row.designation}</td>
                                                <td>{row.doj}</td>
                                                <td>{row.manager}</td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/doctordetails/${row.employee_id}/doctor`)}>
                                                    {row.doctor_count}
                                                </td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/chemistdetails/${row.employee_id}`)}>
                                                    {row.retailer_count}
                                                </td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/stockistdetails/${row.employee_id}`)}>
                                                    {row.stockist_count}
                                                </td>
                                                <td>{row.route_count}</td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/pob-details-doctor-chemist/${row.employee_id}`)}>
                                                    {row.pob_total}
                                                </td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/pob-details-doctor-chemist-datewise/${row.employee_id}`)}>
                                                    {row.pob_total}
                                                </td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/pob-details-doctor-chemist-stockistwise/${row.employee_id}`)}>
                                                    {row.pob_total}
                                                </td>
                                                <td className="pob-data-clickable" 
                                                    onClick={() => handleNavigation(`/pob-details-stockist/${row.employee_id}`)}>
                                                    {row.pob_total}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="17" className="pob-data-no-results">
                                                No data found for the selected filters
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PobData;